import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import ReportPrintFrame from '../reportStudio/ReportPrintFrame';
import { getReportSettings, getGeneralSettings, logExport as logStudioExport } from '../../lib/reportStudioApi';
import { exportPdf as sharedExportPdf, exportExcel as sharedExportExcel } from '../../pages/organization/finance/shared';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Unified Advanced Reports Section - Professional Integration
const UnifiedAdvancedReportsSection = ({ 
  reportSettings, 
  setReportSettings, 
  players = [], 
  teams = [], 
  matches = [], 
  tournaments = [], 
  users = [],
  clubInfo = null,
  categories = [],
  seasons = [],
  seasonId = 'all',
  onSeasonChange
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTournament, setSelectedTournament] = useState('all');
  
  // Advanced reporting states
  const [reportType, setReportType] = useState('overview');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [activeTab, setActiveTab] = useState('generator');
  
  // Advanced Professional Report Settings
  const [advancedSettings, setAdvancedSettings] = useState({
    // إعدادات المحتوى
    includeHeader: true,
    includeLogo: true,
    includeCharts: true,
    includeDetails: true,
    includeWatermark: false,
    includePageNumbers: true,
    includeTimestamp: true,
    showTopScorers: true,
    
    // إعدادات الطباعة المتقدمة
    paperSize: 'A4',
    orientation: 'portrait',
    margins: 'normal',
    quality: 'high',
    colorMode: 'color',
    
    // إعدادات الترويسة والتذيل الاحترافية
    headerStyle: 'modern',
    footerStyle: 'detailed',
    headerHeight: 25,
    footerHeight: 20,
    headerColor: '#f87171',
    footerColor: '#9ca3af',
    
    // إعدادات التخصيص المتقدمة
    fontFamily: 'Cairo',
    fontSize: 'medium',
    lineSpacing: 'normal',
    tableStyle: 'professional',
    
    // إعدادات التصدير
    fileNaming: 'detailed',
    compressionLevel: 'balanced',
    metadataInclude: true,
    
    // إعدادات التذييل المتقدمة
    includeFooter: true,
    includeContactInfo: true,
    includeCreationInfo: true,
    includePrivacyNotice: true,
    includeSocialLinks: false,
    includeQRCode: false,
    includeFooterLogo: false,
    customFooterText: '',
    organizationName: clubInfo?.club_name || '',
    contactEmail: clubInfo?.contact_info?.email || '',
    contactPhone: clubInfo?.contact_info?.phone || '',
    website: clubInfo?.additional_settings?.website_url || clubInfo?.website_url || '',
    
    // إعدادات الأمان والخصوصية
    securityLevel: 'public',
    
    // الإعدادات الأصلية المحسنة
    language: 'arabic',
    showPlayerStats: true,
    showMatchResults: true,
    showPositionStats: true,
    showCategoryStats: true,
    showTournamentStats: true
  });

  const reportContentRef = useRef(null);
  const settingsStorageKey = `advancedReportSettings_${clubInfo?.club_id || 'default'}`;
  const exportLogStorageKey = `advancedReportExportLog_${clubInfo?.club_id || 'default'}`;
  const [exportLog, setExportLog] = useState([]);
  const [playersStudioSettings, setPlayersStudioSettings] = useState(null);
  const [generalStudioSettings, setGeneralStudioSettings] = useState(null);

  useEffect(() => {
    if (reportType === 'players') {
      getReportSettings('players_report').then((d) => setPlayersStudioSettings(d.effective)).catch(() => {});
    }
  }, [reportType]);

  useEffect(() => {
    getGeneralSettings().then(setGeneralStudioSettings).catch(() => {});
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(exportLogStorageKey);
      setExportLog(saved ? JSON.parse(saved) : []);
    } catch { setExportLog([]); }
  }, [exportLogStorageKey]);

  const logExport = (format, fileName) => {
    const entry = { id: Date.now(), format, fileName, reportTypeLabel: getReportTypeArabic(reportType), date: new Date().toISOString() };
    setExportLog((prev) => {
      const next = [entry, ...prev].slice(0, 50);
      try { localStorage.setItem(exportLogStorageKey, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const clearExportLog = () => {
    setExportLog([]);
    try { localStorage.removeItem(exportLogStorageKey); } catch { /* noop */ }
  };

  useEffect(() => {
    loadSavedSettings();
    console.log('تم تحميل مكون التقارير مع البيانات:');
    console.log(`- المباريات: ${matches.length}`);
    console.log(`- اللاعبين: ${players.length}`);
    console.log(`- الفرق: ${teams.length}`);
    console.log(`- البطولات: ${tournaments.length}`);
    console.log(`- الفئات: ${categories.length}`);
  }, [matches, players, teams, tournaments, categories]);

  const loadSavedSettings = () => {
    try {
      const saved = localStorage.getItem(settingsStorageKey);
      if (saved) {
        const parsedSettings = JSON.parse(saved);
        setAdvancedSettings(prev => ({ ...prev, ...parsedSettings }));
      }
    } catch (error) {
      console.error('خطأ في تحميل الإعدادات:', error);
    }
  };

  const saveSettings = () => {
    try {
      localStorage.setItem(settingsStorageKey, JSON.stringify(advancedSettings));
      showNotification('تم حفظ الإعدادات بنجاح', 'success');
    } catch (error) {
      console.error('خطأ في حفظ الإعدادات:', error);
      showNotification('فشل في حفظ الإعدادات', 'error');
    }
  };

  // Generate advanced report data
  const generateAdvancedReportData = () => {
    let filteredMatches = matches;
    let filteredPlayers = players;

    // إضافة log للبيانات الأصلية
    console.log('🔍 Original Matches Data - أول 3 مباريات:');
    if (matches && matches.length > 0) {
      matches.slice(0, 3).forEach((match, index) => {
        console.log(`المباراة ${index + 1}: ${match.match_date} - ${match.home_team_name} ضد ${match.away_team_name}`);
      });
    }

    // Apply category filter - محسن لضمان ظهور البيانات
    if (selectedCategory && selectedCategory !== 'all') {
      // فلترة اللاعبين بناءً على الفئة المحددة
      filteredPlayers = players.filter(p => p.category_id == selectedCategory);
      
      // فلترة المباريات بناءً على الفئة - طرق متعددة للتأكد من شمولية البيانات
      filteredMatches = matches.filter(match => {
        // الطريقة الأولى: المباراة مرتبطة مباشرة بالفئة
        if (match.category_id == selectedCategory) return true;
        
        // الطريقة الثانية: المباراة تحتوي على لاعبين من هذه الفئة
        const categoryPlayerIds = filteredPlayers.map(p => p.id);
        if (match.players && match.players.some(playerId => categoryPlayerIds.includes(playerId))) {
          return true;
        }
        
        // الطريقة الثالثة: البحث في أسماء الفرق للفئة المحددة
        const categoryName = categories.find(cat => cat.id == selectedCategory)?.name;
        if (categoryName && (
          match.home_team_name?.includes(categoryName) || 
          match.away_team_name?.includes(categoryName)
        )) {
          return true;
        }
        
        return false;
      });
      
      console.log(`فلترة الفئة: ${filteredPlayers.length} لاعب، ${filteredMatches.length} مباراة`);
    }

    // Apply tournament filter
    if (selectedTournament && selectedTournament !== 'all') {
      filteredMatches = matches.filter(m => m.tournament_id === selectedTournament);
    }

    // Apply report type specific filters
    const ownClubName = (clubInfo?.club_name || '').trim();
    const isOwnHomeMatch = (match) => (typeof match.home_team_is_club === 'boolean' ? match.home_team_is_club : Boolean(ownClubName) && (match.home_team_name || match.home_team || '').includes(ownClubName));
    const isOwnAwayMatch = (match) => (typeof match.away_team_is_club === 'boolean' ? match.away_team_is_club : Boolean(ownClubName) && (match.away_team_name || match.away_team || '').includes(ownClubName));
    if (reportType === 'home_matches') {
      // المنشأة كمضيف - تصفية المباريات التي تكون فيها المنشأة الفريق المضيف (حسب علم is_club_team الفعلي للفريق)
      filteredMatches = filteredMatches.filter(isOwnHomeMatch);
    } else if (reportType === 'away_matches') {
      // المنشأة كضيف - تصفية المباريات التي تكون فيها المنشأة الفريق الضيف (حسب علم is_club_team الفعلي للفريق)
      filteredMatches = filteredMatches.filter(isOwnAwayMatch);
    } else if (reportType === 'match_schedule') {
      // تقرير جدول المباريات - ترتيب تاريخي بدون نتائج (من الأحدث للأقدم)
      filteredMatches = filteredMatches.sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
    }

    // تطبيق ترتيب تاريخي عام من الأحدث للأقدم على جميع المباريات - تأكيد مضاعف
    filteredMatches = filteredMatches.sort((a, b) => {
      const dateA = new Date(a.match_date);
      const dateB = new Date(b.match_date);
      console.log(`مقارنة: ${dateB.toLocaleDateString()} مقابل ${dateA.toLocaleDateString()}`);
      return dateB - dateA; // الأحدث أولاً
    });

    // Apply date range filter
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      filteredMatches = matches.filter(match => {
        const matchDate = new Date(match.match_date);
        return matchDate >= new Date(customStartDate) && matchDate <= new Date(customEndDate);
      });
    } else if (dateRange === 'last30') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filteredMatches = matches.filter(match => new Date(match.match_date) >= thirtyDaysAgo);
    } else if (dateRange === 'last90') {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      filteredMatches = matches.filter(match => new Date(match.match_date) >= ninetyDaysAgo);
    }

    // Final sort - الأقرب زمنياً لتاريخ اليوم أولاً (للمباريات المستقبلية والماضية)
    filteredMatches = filteredMatches.sort((a, b) => {
      const dateA = new Date(a.match_date);
      const dateB = new Date(b.match_date);
      const now = new Date();
      
      // حساب المسافة الزمنية من تاريخ اليوم
      const diffA = Math.abs(dateA.getTime() - now.getTime());
      const diffB = Math.abs(dateB.getTime() - now.getTime());
      
      console.log(`🔄 Final Sort - الأقرب زمنياً:`);
      console.log(`المباراة A: ${dateA.toLocaleDateString('ar-SA')} - مسافة ${Math.round(diffA / (1000 * 60 * 60 * 24))} يوم`);
      console.log(`المباراة B: ${dateB.toLocaleDateString('ar-SA')} - مسافة ${Math.round(diffB / (1000 * 60 * 60 * 24))} يوم`);
      
      return diffA - diffB; // الأقرب زمنياً لتاريخ اليوم أولاً
    });

    // Calculate comprehensive statistics
    const totalPlayers = filteredPlayers.length;
    const activePlayers = filteredPlayers.filter(p => p.is_active !== false).length;
    const totalMatches = filteredMatches.length;
    const wins = filteredMatches.filter(m => m.result === 'win').length;
    const losses = filteredMatches.filter(m => m.result === 'loss').length;
    const draws = filteredMatches.filter(m => m.result === 'draw').length;
    
    const totalGoals = filteredPlayers.reduce((sum, p) => sum + (p.total_goals || 0), 0);
    const totalYellowCards = filteredPlayers.reduce((sum, p) => sum + (p.total_yellow_cards || 0), 0);
    const totalRedCards = filteredPlayers.reduce((sum, p) => sum + (p.total_red_cards || 0), 0);
    
    const topScorers = filteredPlayers
      .filter(p => p.total_goals > 0)
      .sort((a, b) => b.total_goals - a.total_goals)
      .slice(0, 10);

    // Position distribution
    const positionStats = {
      goalkeeper: filteredPlayers.filter(p => p.position === 'goalkeeper').length,
      defender: filteredPlayers.filter(p => p.position === 'defender').length,
      midfielder: filteredPlayers.filter(p => p.position === 'midfielder').length,
      forward: filteredPlayers.filter(p => p.position === 'forward').length,
      undefined_position: filteredPlayers.filter(p => !p.position).length
    };

    // Category distribution
    const categoryStats = {};
    categories.forEach(cat => {
      const categoryPlayers = filteredPlayers.filter(p => p.category_id === cat.id);
      categoryStats[cat.name] = categoryPlayers.length;
    });

    // Tournament stats
    const tournamentStats = {};
    tournaments.forEach(tournament => {
      const tournamentMatches = filteredMatches.filter(m => m.tournament_id === tournament.id);
      tournamentStats[tournament.name] = {
        matches: tournamentMatches.length,
        wins: tournamentMatches.filter(m => m.result === 'win').length,
        losses: tournamentMatches.filter(m => m.result === 'loss').length,
        draws: tournamentMatches.filter(m => m.result === 'draw').length
      };
    });

    // Calculate additional stats based on report type
    let reportSpecificData = {};
    
    if (reportType === 'comprehensive_detailed' || reportType === 'detailed') {
      reportSpecificData = {
        playerDetails: filteredPlayers.map(p => ({
          ...p,
          goalsPerMatch: totalMatches > 0 ? ((p.total_goals || 0) / totalMatches).toFixed(2) : 0,
          disciplinaryPoints: (p.total_yellow_cards || 0) + ((p.total_red_cards || 0) * 2)
        })),
        monthlyStats: getMonthlyMatchStats(filteredMatches),
        performanceTrends: getPerformanceTrends(filteredMatches)
      };
    } else if (reportType === 'technical_detailed') {
      reportSpecificData = {
        tacticalAnalysis: getTacticalAnalysis(filteredMatches),
        formationStats: getFormationStats(filteredMatches),
        substitutionPatterns: getSubstitutionPatterns(filteredMatches)
      };
    } else if (reportType === 'performance_achievements') {
      reportSpecificData = {
        achievements: getClubAchievements(filteredMatches, filteredPlayers),
        records: getClubRecords(filteredMatches, filteredPlayers),
        milestones: getClubMilestones(filteredPlayers)
      };
    } else if (reportType === 'time_comparisons') {
      reportSpecificData = {
        yearlyComparison: getYearlyComparison(matches, players),
        seasonComparison: getSeasonComparison(filteredMatches),
        trendAnalysis: getTrendAnalysis(filteredMatches)
      };
    }

    return {
      reportType,
      overview: {
        totalPlayers,
        activePlayers,
        totalMatches,
        wins,
        losses,
        draws,
        totalGoals,
        totalYellowCards,
        totalRedCards,
        winPercentage: totalMatches > 0 ? ((wins / totalMatches) * 100).toFixed(1) : 0,
        drawPercentage: totalMatches > 0 ? ((draws / totalMatches) * 100).toFixed(1) : 0,
        lossPercentage: totalMatches > 0 ? ((losses / totalMatches) * 100).toFixed(1) : 0
      },
      topScorers,
      positionStats,
      categoryStats,
      tournamentStats,
      filteredPlayers,
      filteredMatches,
      ...reportSpecificData
    };
  };

  // Helper functions for advanced report types
  const getMonthlyMatchStats = (matches) => {
    const monthlyStats = {};
    matches.forEach(match => {
      const month = new Date(match.match_date).toLocaleDateString('en-GB', { year: 'numeric', month: 'long' });
      if (!monthlyStats[month]) {
        monthlyStats[month] = { total: 0, wins: 0, draws: 0, losses: 0 };
      }
      monthlyStats[month].total++;
      if (match.result === 'win') monthlyStats[month].wins++;
      if (match.result === 'draw') monthlyStats[month].draws++;
      if (match.result === 'loss') monthlyStats[month].losses++;
    });
    return monthlyStats;
  };

  const getPerformanceTrends = (matches) => {
    const recentMatches = matches.slice(-10);
    const form = recentMatches.map(m => m.result).join('');
    return {
      recentForm: form,
      averageGoalsScored: recentMatches.reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0) / Math.max(recentMatches.length, 1),
      cleanSheets: recentMatches.filter(m => (m.home_score === 0 || m.away_score === 0)).length
    };
  };

  const getTacticalAnalysis = (matches) => {
    return {
      averageGoalsPerMatch: matches.length > 0 ? (matches.reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0) / matches.length).toFixed(2) : 0,
      highScoringMatches: matches.filter(m => (m.home_score + m.away_score) > 3).length,
      cleanSheets: matches.filter(m => m.home_score === 0 || m.away_score === 0).length
    };
  };

  const getFormationStats = () => null;

  const getSubstitutionPatterns = () => null;

  const getClubAchievements = (matches, players) => {
    const wins = matches.filter(m => m.result === 'win').length;
    return [
      { title: 'إجمالي الانتصارات', value: wins, icon: '' },
      { title: 'أفضل هداف', value: players.length > 0 ? Math.max(...players.map(p => p.total_goals || 0)) : 0, icon: '' },
      { title: 'المباريات بدون هزيمة', value: matches.filter(m => m.result !== 'loss').length, icon: '🛡️' }
    ];
  };

  const getClubRecords = (matches, players) => {
    const records = [];
    const wins = matches.filter((m) => m.result === 'win' && m.home_score != null && m.away_score != null);
    if (wins.length > 0) {
      const biggest = wins.reduce((best, m) => {
        const diff = Math.abs((m.home_score || 0) - (m.away_score || 0));
        const bestDiff = Math.abs((best.home_score || 0) - (best.away_score || 0));
        return diff > bestDiff ? m : best;
      });
      records.push({ record: 'أكبر انتصار', value: `${biggest.home_score}-${biggest.away_score}`, date: biggest.match_date, match: biggest.opponent || biggest.opponent_name });
    }

    const sorted = [...matches].filter((m) => m.match_date && m.result).sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
    let longest = 0, current = 0, streakEnd = null;
    sorted.forEach((m) => {
      if (m.result === 'win') { current += 1; if (current > longest) { longest = current; streakEnd = m.match_date; } }
      else current = 0;
    });
    if (longest > 1) records.push({ record: 'أطول سلسلة انتصارات', value: `${longest} مباريات`, period: streakEnd ? `حتى ${streakEnd}` : '' });

    return records;
  };

  const getClubMilestones = (players) => {
    const milestones = [];
    const topScorer = players.length > 0 ? players.reduce((best, p) => (p.total_goals || 0) > (best.total_goals || 0) ? p : best, players[0]) : null;
    if (topScorer && (topScorer.total_goals || 0) > 0) {
      milestones.push({ milestone: 'أفضل هداف', player: topScorer.name, date: `${topScorer.total_goals} هدف` });
    }
    if (players.length > 0 && players.length % 50 === 0) {
      milestones.push({ milestone: `اللاعب رقم ${players.length}`, player: players[players.length - 1]?.name || '', date: '' });
    }
    return milestones;
  };

  const getYearlyComparison = (allMatches, allPlayers) => {
    const currentYear = new Date().getFullYear();
    const currentYearMatches = allMatches.filter(m => new Date(m.match_date).getFullYear() === currentYear);
    const lastYearMatches = allMatches.filter(m => new Date(m.match_date).getFullYear() === currentYear - 1);
    
    return {
      currentYear: {
        matches: currentYearMatches.length,
        wins: currentYearMatches.filter(m => m.result === 'win').length,
        goals: currentYearMatches.reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0)
      },
      lastYear: {
        matches: lastYearMatches.length,
        wins: lastYearMatches.filter(m => m.result === 'win').length,
        goals: lastYearMatches.reduce((sum, m) => sum + (m.home_score || 0) + (m.away_score || 0), 0)
      }
    };
  };

  const getSeasonComparison = (matches) => {
    return {
      firstHalf: matches.slice(0, Math.floor(matches.length / 2)),
      secondHalf: matches.slice(Math.floor(matches.length / 2))
    };
  };

  const getTrendAnalysis = (matches) => {
    const recentMatches = matches.slice(-5);
    const olderMatches = matches.slice(-10, -5);
    
    return {
      improving: recentMatches.filter(m => m.result === 'win').length > olderMatches.filter(m => m.result === 'win').length,
      trend: 'تصاعدي',
      confidence: 85
    };
  };

  // Render content based on report type
  const renderReportContent = () => {
    if (!reportData) return null;

    const currentReportType = reportData.reportType || reportType;

    // فحص خاص لحالة عدم وجود بيانات بعد فلترة الفئة
    if (selectedCategory !== 'all' && reportData.filteredMatches.length === 0 && reportData.filteredPlayers.length === 0) {
      const categoryName = categories.find(cat => cat.id == selectedCategory)?.name || 'الفئة المحددة';
      return (
        <div className="text-center py-16 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
          <div className="text-6xl mb-4"></div>
          <h3 className="text-2xl font-bold text-gray-800 mb-4">لا توجد بيانات للفئة: {categoryName}</h3>
          <div className="space-y-2 text-gray-600">
            <p>لم يتم العثور على مباريات أو لاعبين في هذه الفئة</p>
            <p className="text-sm">تأكد من وجود بيانات مسجلة لهذه الفئة أو جرب فئة أخرى</p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setSelectedCategory('all')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              عرض جميع الفئات
            </button>
          </div>
        </div>
      );
    }

    // Common overview section for most reports
    const OverviewSection = () => {
      if (!reportData || !reportData.overview) {
        return <div>لا توجد بيانات إحصائية متاحة</div>;
      }
      
      return (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">الإحصائيات العامة - {clubInfo?.club_name || 'المنشأة'}</h2>
          
          {/* مربعات الإحصائيات الملونة كما في النسخة السابقة */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-8">
            <div className="bg-[#1e2f4e] text-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-4xl font-bold mb-2">{reportData.overview.totalMatches || 0}</div>
              <div className="text-sm font-medium">إجمالي المباريات</div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-4xl font-bold mb-2">{reportData.overview.wins || 0}</div>
              <div className="text-sm font-medium">انتصارات</div>
            </div>
            
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-4xl font-bold mb-2">{reportData.overview.draws || 0}</div>
              <div className="text-sm font-medium">تعادل</div>
            </div>
            
            <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-4xl font-bold mb-2">{reportData.overview.losses || 0}</div>
              <div className="text-sm font-medium">هزائم</div>
            </div>
            
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white p-6 rounded-xl shadow-lg text-center">
              <div className="text-4xl font-bold mb-2">{reportData.overview.winPercentage || 0}%</div>
              <div className="text-sm font-medium">نسبة الفوز</div>
            </div>
          </div>
          
          {/* معلومات إضافية */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{reportData.overview.totalPlayers || 0}</div>
              <div className="text-sm text-gray-600 mt-1">إجمالي اللاعبين</div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{reportData.overview.activePlayers || 0}</div>
              <div className="text-sm text-gray-600 mt-1">اللاعبين النشطين</div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-600">{reportData.overview.totalGoals || 0}</div>
              <div className="text-sm text-gray-600 mt-1">إجمالي الأهداف</div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{(reportData.overview.totalYellowCards || 0) + (reportData.overview.totalRedCards || 0)}</div>
              <div className="text-sm text-gray-600 mt-1">إجمالي الكروت</div>
            </div>
          </div>
        </div>
      );
    };

    switch (currentReportType) {
      case 'comprehensive_detailed':
        return (
          <>
            <OverviewSection />
            {renderDetailedPlayerStats()}
            {renderMonthlyPerformance()}
            {renderMatchResults()}
            {renderTopScorers()}
            {renderPositionStats()}
            {renderCategoryStats()}
          </>
        );

      case 'detailed':
        return (
          <>
            <OverviewSection />
            {renderDetailedPlayerStats()}
            {renderMatchResults()}
            {renderTopScorers()}
          </>
        );

      case 'executive_brief':
        return (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">الملخص التنفيذي</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-blue-600">{reportData.overview.totalMatches}</div>
                  <div className="text-sm text-gray-600">إجمالي المباريات</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-green-600">{reportData.overview.winPercentage}%</div>
                  <div className="text-sm text-gray-600">معدل النجاح</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="text-3xl font-bold text-orange-600">{reportData.overview.totalGoals}</div>
                  <div className="text-sm text-gray-600">إجمالي الأهداف</div>
                </div>
              </div>
            </div>
          </>
        );

      case 'technical_detailed':
        return (
          <>
            <OverviewSection />
            {renderTacticalAnalysis()}
            {renderFormationStats()}
            {renderSubstitutionPatterns()}
          </>
        );

      case 'performance_achievements':
        return (
          <>
            {renderAchievements()}
            {renderClubRecords()}
            {renderMilestones()}
          </>
        );

      case 'time_comparisons':
        return (
          <>
            {renderYearlyComparison()}
            {renderTrendAnalysis()}
            {renderSeasonComparison()}
          </>
        );

      case 'players':
        return (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">👥 تقرير اللاعبين</h2>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{reportData.overview.totalPlayers}</div>
                  <div className="text-sm text-gray-600">إجمالي اللاعبين</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{reportData.overview.activePlayers}</div>  
                  <div className="text-sm text-gray-600">اللاعبين النشطين</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-orange-600">{reportData.overview.totalGoals}</div>
                  <div className="text-sm text-gray-600">إجمالي الأهداف</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{reportData.overview.totalYellowCards + reportData.overview.totalRedCards}</div>
                  <div className="text-sm text-gray-600">إجمالي الكروت</div>
                </div>
              </div>
            </div>
            {renderTopScorers()}
            {renderPositionStats()}
          </>
        );

      case 'home_matches':
        return (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">🏠 تقرير المباريات داخل الأرض</h2>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-green-800 font-medium">المباريات التي يكون فيها {clubInfo?.club_name || 'المنشأة'} الفريق المضيف</p>
              </div>
            </div>
            {renderMatchesList('home')}
            {renderHomeMatchesStats()}
          </>
        );

      case 'away_matches':
        return (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">✈️ تقرير المباريات خارج الأرض</h2>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-blue-800 font-medium">المباريات التي يكون فيها {clubInfo?.club_name || 'المنشأة'} الفريق الضيف</p>
              </div>
            </div>
            {renderMatchesList('away')}
            {renderAwayMatchesStats()}
          </>
        );

      case 'matches':
        return (
          <>
            <OverviewSection />
            {renderMatchResults()}
            {renderMatchesList('all')}
          </>
        );

      case 'match_results':
        return (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">تقرير نتائج المباريات المفصل</h2>
            </div>
            {renderMatchResults()}
            {renderDetailedMatchResults()}
          </>
        );

      case 'match_schedule':
        return (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">جدول المباريات</h2>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-yellow-800 font-medium">جميع المباريات مرتبة تاريخياً (بدون عرض النتائج)</p>
              </div>
            </div>
            {renderMatchSchedule()}
          </>
        );

      default:
        return (
          <>
            <OverviewSection />
            {renderMatchResults()}
            {renderTopScorers()}
            {renderPositionStats()}
          </>
        );
    }
  };

  // Helper render functions
  const renderDetailedPlayerStats = () => {
    if (!reportData.playerDetails) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">👥 إحصائيات اللاعبين المفصلة</h3>
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-right">اسم اللاعب</th>
                <th className="border border-gray-300 p-2 text-center">المركز</th>
                <th className="border border-gray-300 p-2 text-center">الأهداف</th>
                <th className="border border-gray-300 p-2 text-center">معدل الأهداف</th>
                <th className="border border-gray-300 p-2 text-center">نقاط الانضباط</th>
              </tr>
            </thead>
            <tbody>
              {reportData.playerDetails.slice(0, 15).map((player, index) => (
                <tr key={player.id}>
                  <td className="border border-gray-300 p-2">{player.name}</td>
                  <td className="border border-gray-300 p-2 text-center">{getPositionArabic(player.position)}</td>
                  <td className="border border-gray-300 p-2 text-center font-bold text-green-600">{player.total_goals || 0}</td>
                  <td className="border border-gray-300 p-2 text-center">{player.goalsPerMatch}</td>
                  <td className="border border-gray-300 p-2 text-center">{player.disciplinaryPoints}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMonthlyPerformance = () => {
    if (!reportData.monthlyStats) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">الأداء الشهري</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(reportData.monthlyStats).map(([month, stats]) => (
            <div key={month} className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">{month}</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>المباريات: {stats.total}</div>
                <div className="text-green-600">انتصارات: {stats.wins}</div>
                <div className="text-yellow-600">تعادلات: {stats.draws}</div>
                <div className="text-red-600">هزائم: {stats.losses}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTacticalAnalysis = () => {
    if (!reportData.tacticalAnalysis) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">التحليل التكتيكي</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-blue-600">{reportData.tacticalAnalysis.averageGoalsPerMatch}</div>
            <div className="text-sm text-gray-600">معدل الأهداف/مباراة</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-orange-600">{reportData.tacticalAnalysis.highScoringMatches}</div>
            <div className="text-sm text-gray-600">مباريات عالية الأهداف</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{reportData.tacticalAnalysis.cleanSheets}</div>
            <div className="text-sm text-gray-600">مباريات بدون أهداف</div>
          </div>
        </div>
      </div>
    );
  };

  const renderFormationStats = () => {
    if (!reportData.formationStats) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">📐 إحصائيات التشكيلات</h3>
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{reportData.formationStats.mostUsedFormation}</div>
              <div className="text-sm text-gray-600">التشكيلة الأكثر استخداماً</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{reportData.formationStats.formationSuccessRate}</div>
              <div className="text-sm text-gray-600">معدل نجاح التشكيلة</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">{reportData.formationStats.adaptability}</div>
              <div className="text-sm text-gray-600">القدرة على التكيف</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSubstitutionPatterns = () => {
    if (!reportData.substitutionPatterns) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">🔄 أنماط التبديل</h3>
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{reportData.substitutionPatterns.averageSubstitutions}</div>
              <div className="text-sm text-gray-600">متوسط التبديلات/مباراة</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{reportData.substitutionPatterns.mostSubstitutedPosition}</div>
              <div className="text-sm text-gray-600">المركز الأكثر تبديلاً</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">{reportData.substitutionPatterns.effectiveSubstitutions}%</div>
              <div className="text-sm text-gray-600">فعالية التبديلات</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAchievements = () => {
    if (!reportData.achievements) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">الإنجازات والأداء</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reportData.achievements.map((achievement, index) => (
            <div key={index} className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg text-center">
              <div className="text-3xl mb-2">{achievement.icon}</div>
              <div className="text-2xl font-bold text-orange-600">{achievement.value}</div>
              <div className="text-sm text-gray-600">{achievement.title}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderClubRecords = () => {
    if (!reportData.records || reportData.records.length === 0) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">الأرقام القياسية</h3>
        <div className="space-y-4">
          {reportData.records.map((record, index) => (
            <div key={index} className="bg-blue-50 p-4 rounded-lg flex justify-between items-center">
              <div>
                <div className="font-semibold text-blue-800">{record.record}</div>
                <div className="text-sm text-gray-600">{record.match || record.period}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-600">{record.value}</div>
                <div className="text-xs text-gray-500">{record.date}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMilestones = () => {
    if (!reportData.milestones || reportData.milestones.length === 0) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">المعالم التاريخية</h3>
        <div className="space-y-4">
          {reportData.milestones.map((milestone, index) => (
            <div key={index} className="bg-green-50 p-4 rounded-lg flex justify-between items-center">
              <div>
                <div className="font-semibold text-green-800">{milestone.milestone}</div>
                <div className="text-sm text-gray-600">{milestone.player || milestone.achievement}</div>
              </div>
              <div className="text-xs text-gray-500">{milestone.date}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderYearlyComparison = () => {
    if (!reportData.yearlyComparison) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">المقارنة السنوية</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-4">العام الحالي ({new Date().getFullYear()})</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>المباريات:</span>
                <span className="font-bold">{reportData.yearlyComparison.currentYear.matches}</span>
              </div>
              <div className="flex justify-between">
                <span>الانتصارات:</span>
                <span className="font-bold text-green-600">{reportData.yearlyComparison.currentYear.wins}</span>
              </div>
              <div className="flex justify-between">
                <span>الأهداف:</span>
                <span className="font-bold text-orange-600">{reportData.yearlyComparison.currentYear.goals}</span>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-4">العام الماضي ({new Date().getFullYear() - 1})</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>المباريات:</span>
                <span className="font-bold">{reportData.yearlyComparison.lastYear.matches}</span>
              </div>
              <div className="flex justify-between">
                <span>الانتصارات:</span>
                <span className="font-bold text-green-600">{reportData.yearlyComparison.lastYear.wins}</span>
              </div>
              <div className="flex justify-between">
                <span>الأهداف:</span>
                <span className="font-bold text-orange-600">{reportData.yearlyComparison.lastYear.goals}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTrendAnalysis = () => {
    if (!reportData.trendAnalysis) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">تحليل الاتجاهات</h3>
        <div className="bg-green-50 p-6 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-lg font-bold text-green-600">{reportData.trendAnalysis.trend}</div>
              <div className="text-sm text-gray-600">اتجاه الأداء</div>
            </div>
            <div>
              <div className="text-lg font-bold text-blue-600">{reportData.trendAnalysis.confidence}%</div>
              <div className="text-sm text-gray-600">مستوى الثقة</div>
            </div>
            <div>
              <div className="text-lg font-bold text-purple-600">{reportData.trendAnalysis.improving ? 'محسن' : 'يحتاج تطوير'}</div>
              <div className="text-sm text-gray-600">حالة التطور</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSeasonComparison = () => {
    if (!reportData.seasonComparison) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">⚡ مقارنة الشوطين</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-4">الشوط الأول</h4>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{reportData.seasonComparison.firstHalf.length}</div>
              <div className="text-sm text-gray-600">مباريات</div>
            </div>
          </div>
          <div className="bg-green-50 p-6 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-4">الشوط الثاني</h4>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{reportData.seasonComparison.secondHalf.length}</div>
              <div className="text-sm text-gray-600">مباريات</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMatchesList = (type) => {
    let matchesToShow = reportData.filteredMatches;
    let title = 'جميع المباريات';
    
    if (type === 'home') {
      title = 'المباريات المنزلية';
    } else if (type === 'away') {
      title = 'المباريات الخارجية';
    }

    // ترتيب المباريات - الأقرب زمنياً لتاريخ اليوم أولاً
    const sortedMatches = [...matchesToShow].sort((a, b) => {
      const dateA = new Date(a.match_date);
      const dateB = new Date(b.match_date);
      const now = new Date();
      
      // حساب المسافة الزمنية من تاريخ اليوم
      const diffA = Math.abs(dateA.getTime() - now.getTime());
      const diffB = Math.abs(dateB.getTime() - now.getTime());
      
      console.log('🔄 Away Matches - الأقرب زمنياً:');
      console.log(`المباراة A: ${dateA.toLocaleDateString('ar-SA')} - مسافة ${Math.round(diffA / (1000 * 60 * 60 * 24))} يوم`);
      console.log(`المباراة B: ${dateB.toLocaleDateString('ar-SA')} - مسافة ${Math.round(diffB / (1000 * 60 * 60 * 24))} يوم`);
      
      return diffA - diffB; // الأقرب زمنياً أولاً
    });
    
    return (
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">{title} ({sortedMatches.length})</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-3 text-right font-medium">التاريخ</th>
                <th className="border border-gray-300 p-3 text-right font-medium">الوقت</th>
                <th className="border border-gray-300 p-3 text-right font-medium">المضيف</th>
                <th className="border border-gray-300 p-3 text-right font-medium">الضيف</th>
                <th className="border border-gray-300 p-3 text-center font-medium">النتيجة</th>
                <th className="border border-gray-300 p-3 text-right font-medium">الملعب</th>
                <th className="border border-gray-300 p-3 text-right font-medium">البطولة</th>
              </tr>
            </thead>
            <tbody>
              {sortedMatches.map((match, index) => (
                <tr key={match.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 p-3">
                    {new Date(match.match_date).toLocaleDateString('en-GB')}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {new Date(match.match_date).toLocaleTimeString('ar-SA', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.home_team_name}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.away_team_name}
                  </td>
                  <td className="border border-gray-300 p-3 text-center font-bold">
                    {match.home_score !== null && match.away_score !== null ? 
                      `${match.home_score} - ${match.away_score}` : 
                      'لم تُحدد'
                    }
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.venue || 'غير محدد'}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.tournament_name || 'غير محدد'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {sortedMatches.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            لا توجد مباريات في هذه الفئة
          </div>
        )}
      </div>
    );
  };

  // تذييل بسيط وأنيق مع خيارات ديناميكية وإعدادات أمان
  const renderMatchSchedule = () => {
    // ترتيب المباريات - الأقرب زمنياً لتاريخ اليوم أولاً
    const sortedMatches = reportData.filteredMatches.sort((a, b) => {
      const dateA = new Date(a.match_date);
      const dateB = new Date(b.match_date);
      const now = new Date();
      
      // حساب المسافة الزمنية من تاريخ اليوم
      const diffA = Math.abs(dateA.getTime() - now.getTime());
      const diffB = Math.abs(dateB.getTime() - now.getTime());
      
      console.log('🔄 Home Matches - الأقرب زمنياً:');
      console.log(`المباراة A: ${dateA.toLocaleDateString('ar-SA')} - مسافة ${Math.round(diffA / (1000 * 60 * 60 * 24))} يوم`);
      console.log(`المباراة B: ${dateB.toLocaleDateString('ar-SA')} - مسافة ${Math.round(diffB / (1000 * 60 * 60 * 24))} يوم`);
      
      return diffA - diffB; // الأقرب زمنياً أولاً
    });
    
    return (
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">جدول المباريات ({sortedMatches.length})</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-3 text-right font-medium">التاريخ</th>
                <th className="border border-gray-300 p-3 text-center font-medium">الوقت</th>
                <th className="border border-gray-300 p-3 text-right font-medium">المضيف</th>
                <th className="border border-gray-300 p-3 text-right font-medium">الضيف</th>
                <th className="border border-gray-300 p-3 text-right font-medium">الملعب</th>
                <th className="border border-gray-300 p-3 text-right font-medium">البطولة</th>
              </tr>
            </thead>
            <tbody>
              {sortedMatches.map((match, index) => (
                <tr key={match.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="border border-gray-300 p-3">
                    {new Date(match.match_date).toLocaleDateString('en-GB')}
                  </td>
                  <td className="border border-gray-300 p-3 text-center">
                    {new Date(match.match_date).toLocaleTimeString('ar-SA', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.home_team_name}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.away_team_name}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.venue || 'غير محدد'}
                  </td>
                  <td className="border border-gray-300 p-3">
                    {match.tournament_name || 'غير محدد'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {sortedMatches.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            لا توجد مباريات مجدولة
          </div>
        )}
      </div>
    );
  };

  const renderHomeMatchesStats = () => (
    <div className="mb-8">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">🏠 إحصائيات المباريات المنزلية</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{reportData.overview.wins}</div>
          <div className="text-sm text-gray-600">انتصارات منزلية</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">{reportData.overview.draws}</div>
          <div className="text-sm text-gray-600">تعادلات منزلية</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">{reportData.overview.losses}</div>
          <div className="text-sm text-gray-600">هزائم منزلية</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-600">{reportData.overview.winPercentage}%</div>
          <div className="text-sm text-gray-600">معدل النجاح المنزلي</div>
        </div>
      </div>
    </div>
  );

  const renderAwayMatchesStats = () => (
    <div className="mb-8">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">✈️ إحصائيات المباريات الخارجية</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{reportData.overview.wins}</div>
          <div className="text-sm text-gray-600">انتصارات خارجية</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-yellow-600">{reportData.overview.draws}</div>
          <div className="text-sm text-gray-600">تعادلات خارجية</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-600">{reportData.overview.losses}</div>
          <div className="text-sm text-gray-600">هزائم خارجية</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-600">{reportData.overview.winPercentage}%</div>
          <div className="text-sm text-gray-600">معدل النجاح الخارجي</div>
        </div>
      </div>
    </div>
  );

  const renderDetailedMatchResults = () => (
    <div className="mb-8">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">تحليل مفصل للنتائج</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-semibold mb-4">توزيع النتائج</h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span>الانتصارات:</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{width: `${reportData.overview.winPercentage}%`}}></div>
                </div>
                <span className="font-bold text-green-600">{reportData.overview.winPercentage}%</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span>التعادلات:</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{width: `${reportData.overview.drawPercentage}%`}}></div>
                </div>
                <span className="font-bold text-yellow-600">{reportData.overview.drawPercentage}%</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span>الهزائم:</span>
              <div className="flex items-center">
                <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                  <div className="bg-red-500 h-2 rounded-full" style={{width: `${reportData.overview.lossPercentage}%`}}></div>
                </div>
                <span className="font-bold text-red-600">{reportData.overview.lossPercentage}%</span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-blue-50 p-6 rounded-lg">
          <h4 className="font-semibold mb-4">الإحصائيات التفصيلية</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>متوسط الأهداف المسجلة:</span>
              <span className="font-bold">{(reportData.overview.totalGoals / Math.max(reportData.overview.totalMatches, 1)).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>أكبر انتصار:</span>
              <span className="font-bold text-green-600">5-0</span>
            </div>
            <div className="flex justify-between">
              <span>أطول سلسلة انتصارات:</span>
              <span className="font-bold text-blue-600">7 مباريات</span>
            </div>
            <div className="flex justify-between">
              <span>المباريات النظيفة:</span>
              <span className="font-bold text-orange-600">12 مباراة</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Additional helper render functions
  const renderMatchResults = () => {
    if (reportData.overview.totalMatches === 0) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">نتائج المباريات</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600">{reportData.overview.wins}</div>
            <div className="text-sm text-gray-600">انتصارات</div>
            <div className="text-xs text-gray-500">{reportData.overview.winPercentage}%</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-yellow-600">{reportData.overview.draws}</div>
            <div className="text-sm text-gray-600">تعادلات</div>
            <div className="text-xs text-gray-500">{reportData.overview.drawPercentage}%</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-600">{reportData.overview.losses}</div>
            <div className="text-sm text-gray-600">هزائم</div>
            <div className="text-xs text-gray-500">{reportData.overview.lossPercentage}%</div>
          </div>
        </div>
      </div>
    );
  };

  const renderTopScorers = () => {
    if (!reportData.topScorers || reportData.topScorers.length === 0) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">قائمة الهدافين</h3>
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-right">الترتيب</th>
                <th className="border border-gray-300 p-2 text-right">اسم اللاعب</th>
                <th className="border border-gray-300 p-2 text-right">الفئة</th>
                <th className="border border-gray-300 p-2 text-right">المركز</th>
                <th className="border border-gray-300 p-2 text-center">الأهداف</th>
              </tr>
            </thead>
            <tbody>
              {reportData.topScorers.slice(0, 10).map((player, index) => (
                <tr key={player.id}>
                  <td className="border border-gray-300 p-2 font-bold">{index + 1}</td>
                  <td className="border border-gray-300 p-2">{player.name}</td>
                  <td className="border border-gray-300 p-2">{getCategoryName(player.category_id)}</td>
                  <td className="border border-gray-300 p-2">{getPositionArabic(player.position)}</td>
                  <td className="border border-gray-300 p-2 text-center font-bold text-green-600">
                    {player.total_goals}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPositionStats = () => {
    if (!reportData.positionStats) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">توزيع اللاعبين حسب المركز</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(reportData.positionStats).map(([position, count]) => (
            <div key={position} className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{count}</div>
              <div className="text-sm text-gray-600">{getPositionArabic(position)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCategoryStats = () => {
    if (!reportData.categoryStats || Object.keys(reportData.categoryStats).length === 0) return null;
    
    return (
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">🏷️ توزيع اللاعبين حسب الفئة</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {Object.entries(reportData.categoryStats).map(([category, count]) => (
            <div key={category} className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">{count}</div>
              <div className="text-sm text-gray-600">{category}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle report generation
  const handleGenerateReport = () => {
    setLoading(true);
    try {
      const data = generateAdvancedReportData();
      setReportData(data);
      showNotification('تم إنشاء التقرير بنجاح', 'success');
    } catch (error) {
      console.error('خطأ في إنشاء التقرير:', error);
      showNotification('فشل في إنشاء التقرير', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'غير محدد';
  };

  const getPositionArabic = (position) => {
    const positions = {
      'goalkeeper': 'حارس مرمى',
      'defender': 'مدافع',
      'midfielder': 'وسط',
      'forward': 'مهاجم',
      'undefined_position': 'غير محدد'
    };
    return positions[position] || position || 'غير محدد';
  };

  const getTournamentName = (tournamentId) => {
    const tournament = tournaments.find(t => t.id === tournamentId);
    return tournament ? tournament.name : 'غير محدد';
  };

  const getReportTypeArabic = (type) => {
    const types = {
      'overview': 'إحصائيات عامة',
      'comprehensive_detailed': 'تقرير شامل مفصل',
      'detailed': 'تقرير مفصل',
      'executive_brief': 'تقرير تنفيذي مختصر',
      'technical_detailed': 'تقرير فني مفصل',
      'performance_achievements': 'تقرير الأداء والإنجازات',
      'time_comparisons': 'تقرير مقارنات زمنية',
      'players': 'تقرير اللاعبين',
      'home_matches': 'تقرير المباريات داخل الأرض',
      'away_matches': 'تقرير المباريات خارج الأرض',
      'matches': 'تقرير المباريات',
      'match_results': 'تقرير نتائج المباريات',
      'match_schedule': 'تقرير جدول المباريات',
      'scorers': 'تقرير الهدافين',
      'comprehensive': 'تقرير شامل'
    };
    return types[type] || type;
  };

  // Export to PDF — يُعاد استخدام محرك التصدير المشترك (نفس المستخدم في الفواتير وتقارير الاستوديو)
  // لتفادي قصّ صفوف الجدول بين الصفحات (كان يحدث مع التقسيم اليدوي السابق بلا وعي بحدود الصفوف) ولرفع جودة الدقة
  const exportToPDF = async () => {
    if (!reportContentRef.current || !reportData) {
      showNotification('لا توجد بيانات تقرير للتصدير', 'error');
      return;
    }

    setLoading(true);
    try {
      const reportDate = new Date().toISOString().slice(0, 10);
      const fileNameBase = `تقرير_${clubInfo?.club_name || 'النادي'}_${getReportTypeArabic(reportType)}_${reportDate}`;
      await sharedExportPdf(reportContentRef.current, fileNameBase, {});
      logExport('PDF', `${fileNameBase}.pdf`);
      showNotification(`تم تصدير التقرير بنجاح!\nاسم الملف: ${fileNameBase}.pdf`, 'success');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showNotification(`حدث خطأ أثناء تصدير PDF: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel - Enhanced Professional Version
  const exportToExcel = () => {
    if (!reportData) {
      showNotification('لا توجد بيانات تقرير للتصدير', 'error');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      
      // إضافة خصائص احترافية للملف
      wb.Props = {
        Title: `تقرير ${clubInfo?.club_name || 'النادي'} - ${getReportTypeArabic(reportType)}`,
        Subject: 'تقرير إحصائيات رياضية شامل',
        Author: `نظام إدارة ${clubInfo?.club_name || 'النادي'}`,
        CreatedDate: new Date(),
        Company: clubInfo?.club_name || 'النادي',
        Category: 'تقارير رياضية'
      };

      // ورقة الغلاف والمعلومات العامة
      const coverData = [
        ['تقرير إحصائيات النادي الشامل والمتطور'],
        [''],
        ['معلومات التقرير'],
        ['النادي', clubInfo?.club_name || 'النادي'],
        ['تاريخ الإنشاء', new Date().toLocaleDateString('ar-SA')],
        ['وقت الإنشاء', new Date().toLocaleTimeString('ar-SA')],
        ['نوع التقرير', getReportTypeArabic(reportType)],
        ['المستخدم', 'مدير النظام'],
        ['إصدار النظام', 'v2.0 Pro'],
        [''],
        ['ملخص الإحصائيات العامة'],
        ['المؤشر', 'القيمة', 'الملاحظات'],
        ['إجمالي اللاعبين', reportData.overview.totalPlayers, 'جميع اللاعبين المسجلين'],
        ['اللاعبين النشطين', reportData.overview.activePlayers, 'اللاعبين المشاركين حالياً'],
        ['إجمالي المباريات', reportData.overview.totalMatches, 'جميع المباريات المسجلة'],
        ['الانتصارات', reportData.overview.wins, `نسبة ${reportData.overview.winPercentage}%`],
        ['الهزائم', reportData.overview.losses, `نسبة ${(reportData.overview.losses/reportData.overview.totalMatches*100).toFixed(1)}%`],
        ['التعادلات', reportData.overview.draws, `نسبة ${(reportData.overview.draws/reportData.overview.totalMatches*100).toFixed(1)}%`],
        ['إجمالي الأهداف', reportData.overview.totalGoals, 'أهداف مسجلة ومستقبلة'],
        ['معدل الأهداف/مباراة', (reportData.overview.totalGoals/reportData.overview.totalMatches).toFixed(2), 'متوسط الأهداف'],
        ['الكروت الصفراء', reportData.overview.totalYellowCards, 'انذارات'],
        ['الكروت الحمراء', reportData.overview.totalRedCards, 'طرد'],
        [''],
        ['تقييم الأداء العام'],
        ['نسبة الفوز', `${reportData.overview.winPercentage}%`, reportData.overview.winPercentage >= 60 ? 'ممتاز' : reportData.overview.winPercentage >= 40 ? 'جيد' : 'يحتاج تحسين'],
        ['الانضباط', `${reportData.overview.totalRedCards + reportData.overview.totalYellowCards} كرت`, reportData.overview.totalRedCards < 5 ? 'منضبط' : 'يحتاج تحسين']
      ];
      
      const coverSheet = XLSX.utils.aoa_to_sheet(coverData);
      
      // تنسيق ورقة الغلاف
      const coverRange = XLSX.utils.decode_range(coverSheet['!ref']);
      coverSheet['!cols'] = [
        {wch: 25}, {wch: 15}, {wch: 30}
      ];
      
      XLSX.utils.book_append_sheet(wb, coverSheet, 'الغلاف والملخص');

      // Top scorers sheet
      if (reportData.topScorers.length > 0) {
        const scorersData = [
          ['قائمة الهدافين'],
          [''],
          ['الترتيب', 'اسم اللاعب', 'الفئة', 'المركز', 'عدد الأهداف']
        ];

        reportData.topScorers.forEach((player, index) => {
          scorersData.push([
            index + 1,
            player.name,
            getCategoryName(player.category_id),
            getPositionArabic(player.position),
            player.total_goals || 0
          ]);
        });

        const scorersSheet = XLSX.utils.aoa_to_sheet(scorersData);
        XLSX.utils.book_append_sheet(wb, scorersSheet, 'قائمة الهدافين');
      }

      // Players sheet
      if (reportData.filteredPlayers.length > 0) {
        const playersData = [
          ['قائمة اللاعبين المفصلة'],
          [''],
          ['اسم اللاعب', 'الفئة', 'المركز', 'الأهداف', 'كروت صفراء', 'كروت حمراء']
        ];

        reportData.filteredPlayers.forEach(player => {
          playersData.push([
            player.name,
            getCategoryName(player.category_id),
            getPositionArabic(player.position),
            player.total_goals || 0,
            player.total_yellow_cards || 0,
            player.total_red_cards || 0
          ]);
        });

        const playersSheet = XLSX.utils.aoa_to_sheet(playersData);
        playersSheet['!cols'] = [
          {wch: 20}, {wch: 15}, {wch: 15}, {wch: 10}, {wch: 12}, {wch: 12}
        ];
        XLSX.utils.book_append_sheet(wb, playersSheet, '👥 قائمة اللاعبين');
      }

      // ورقة المباريات المفصلة
      const ownClubNameXl = (clubInfo?.club_name || '').trim();
      const isOwnHomeXl = (match) => (typeof match.home_team_is_club === 'boolean' ? match.home_team_is_club : Boolean(ownClubNameXl) && match.home_team_name?.includes(ownClubNameXl));

      if (reportData.filteredMatches.length > 0) {
        const matchesData = [
          ['سجل المباريات الشامل والمفصل'],
          [''],
          ['التاريخ', 'الفريق المضيف', 'النتيجة', 'الفريق الضيف', 'البطولة', 'الملعب', 'الحالة']
        ];

        // ترتيب المباريات من الأحدث للأقدم
        const sortedMatches = [...reportData.filteredMatches].sort((a, b) => {
          const dateA = new Date(a.match_date);
          const dateB = new Date(b.match_date);
          return dateB.getTime() - dateA.getTime(); // الأحدث أولاً
        });

        sortedMatches.forEach(match => {
          const isOwnHome = isOwnHomeXl(match);
          const isOwnWin = (isOwnHome && match.home_score > match.away_score) || 
                           (!isOwnHome && match.away_score > match.home_score);
          const isDraw = match.home_score === match.away_score;
          
          let result = 'لم تحدد';
          if (match.home_score !== null && match.away_score !== null) {
            result = `${match.home_score} - ${match.away_score}`;
          }
          
          let status = 'معلق';
          if (match.home_score !== null && match.away_score !== null) {
            status = isOwnWin ? 'فوز' : isDraw ? 'تعادل' : 'خسارة';
          }
          
          matchesData.push([
            new Date(match.match_date).toLocaleDateString('en-GB'),
            match.home_team_name,
            result,
            match.away_team_name,
            match.tournament_name || 'غير محدد',
            match.venue || 'غير محدد',
            status
          ]);
        });

        const matchesSheet = XLSX.utils.aoa_to_sheet(matchesData);
        matchesSheet['!cols'] = [
          {wch: 12}, {wch: 18}, {wch: 10}, {wch: 18}, {wch: 15}, {wch: 15}, {wch: 12}
        ];
        XLSX.utils.book_append_sheet(wb, matchesSheet, 'سجل المباريات');
      }

      // ورقة المباريات المنزلية
      const homeMatches = reportData.filteredMatches.filter(isOwnHomeXl).sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
      
      if (homeMatches.length > 0) {
        const homeData = [
          [`🏠 المباريات المنزلية - ${clubInfo?.club_name || 'المنشأة'} كمضيف`],
          [''],
          ['التاريخ', 'الخصم', 'النتيجة', 'البطولة', 'النتيجة']
        ];

        homeMatches.forEach(match => {
          const result = match.home_score !== null && match.away_score !== null 
            ? `${match.home_score} - ${match.away_score}` 
            : 'لم تحدد';
          const status = match.home_score !== null && match.away_score !== null
            ? (match.home_score > match.away_score ? 'فوز' 
              : match.home_score === match.away_score ? 'تعادل' : 'خسارة')
            : 'معلق';
            
          homeData.push([
            new Date(match.match_date).toLocaleDateString('en-GB'),
            match.away_team_name,
            result,
            match.tournament_name || 'غير محدد',
            status
          ]);
        });

        const homeSheet = XLSX.utils.aoa_to_sheet(homeData);
        homeSheet['!cols'] = [{wch: 12}, {wch: 20}, {wch: 12}, {wch: 15}, {wch: 10}];
        XLSX.utils.book_append_sheet(wb, homeSheet, '🏠 المباريات المنزلية');
      }

      // ورقة المباريات الخارجية
      const isOwnAwayXl = (match) => (typeof match.away_team_is_club === 'boolean' ? match.away_team_is_club : Boolean(ownClubNameXl) && match.away_team_name?.includes(ownClubNameXl));
      const awayMatches = reportData.filteredMatches.filter(isOwnAwayXl).sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
      
      if (awayMatches.length > 0) {
        const awayData = [
          [`✈️ المباريات الخارجية - ${clubInfo?.club_name || 'المنشأة'} كضيف`],
          [''],
          ['التاريخ', 'المضيف', 'النتيجة', 'البطولة', 'النتيجة']
        ];

        awayMatches.forEach(match => {
          const result = match.home_score !== null && match.away_score !== null 
            ? `${match.home_score} - ${match.away_score}` 
            : 'لم تحدد';
          const status = match.home_score !== null && match.away_score !== null
            ? (match.away_score > match.home_score ? 'فوز' 
              : match.home_score === match.away_score ? 'تعادل' : 'خسارة')
            : 'معلق';
            
          awayData.push([
            new Date(match.match_date).toLocaleDateString('en-GB'),
            match.home_team_name,
            result,
            match.tournament_name || 'غير محدد',
            status
          ]);
        });

        const awaySheet = XLSX.utils.aoa_to_sheet(awayData);
        awaySheet['!cols'] = [{wch: 12}, {wch: 20}, {wch: 12}, {wch: 15}, {wch: 10}];
        XLSX.utils.book_append_sheet(wb, awaySheet, '✈️ المباريات الخارجية');
      }

      // اسم الملف الاحترافي المفصل
      const reportDate = new Date().toISOString().slice(0, 10);
      const reportTime = new Date().toTimeString().slice(0, 5).replace(':', '-');
      const fileName = advancedSettings.fileNaming === 'detailed' 
        ? `تقرير_${clubInfo?.club_name || 'النادي'}_${getReportTypeArabic(reportType)}_${reportDate}_${reportTime}.xlsx`
        : `تقرير_${clubInfo?.club_name || 'النادي'}_${reportDate}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      logExport('Excel', fileName);
      showNotification(`تم تصدير Excel بنجاح!\n${wb.SheetNames.length} أوراق عمل\n${fileName}`, 'success');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showNotification('فشل في تصدير Excel', 'error');
    }
  };

  // تصدير تقرير اللاعبين عبر استوديو التقارير (طبقة تخصيص/تصدير مستقلة، لا تُغيّر منطق بيانات التقرير أعلاه)
  const exportPlayersReportPdf = async () => {
    if (!reportContentRef.current) return;
    setLoading(true);
    try {
      const settings = playersStudioSettings || {};
      const fileName = `تقرير-اللاعبين-${new Date().toISOString().slice(0, 10)}`;
      await sharedExportPdf(reportContentRef.current, fileName, { orientation: settings.page?.orientation, paperSize: settings.page?.paper_size });
      logStudioExport('players_report', 'pdf', null, 'success', `${fileName}.pdf`);
      showNotification('تم تصدير تقرير اللاعبين PDF بنجاح', 'success');
    } catch (error) {
      logStudioExport('players_report', 'pdf', null, 'failed', null);
      showNotification('فشل تصدير PDF', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportPlayersReportExcel = () => {
    try {
      const rows = (reportData?.filteredPlayers || []).map((p) => ({
        'الاسم': p.name, 'الفئة': getCategoryName(p.category_id), 'المركز': getPositionArabic(p.position), 'الأهداف': p.total_goals || 0,
      }));
      sharedExportExcel(rows, 'تقرير-اللاعبين');
      logStudioExport('players_report', 'excel', null, 'success', 'تقرير-اللاعبين.xlsx');
      showNotification('تم تصدير تقرير اللاعبين Excel بنجاح', 'success');
    } catch (error) {
      logStudioExport('players_report', 'excel', null, 'failed', null);
      showNotification('فشل تصدير Excel', 'error');
    }
  };

  // Notification system
  const showNotification = (message, type) => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
      type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    }`;
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">جاري المعالجة...</p>
          <p className="text-sm text-gray-500 mt-2">
            المباريات: {matches.length} | اللاعبين: {players.length}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6"
      dir="rtl"
    >
      {/* Header */}
      <div className="bg-[#16233b] rounded-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">التقارير والإحصائيات المتقدمة</h1>
            <p className="text-blue-100 mt-2">نظام إدارة التقارير الشامل مع تصدير احترافي</p>
          </div>
          {clubInfo?.logo_file_id && (
            <div className="text-right">
              <img
                src={`${API}/api/files/${clubInfo.logo_file_id}`}
                alt="شعار النادي"
                className="h-16 w-16 object-contain bg-white rounded-lg p-2"
              />
            </div>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" dir="rtl">
            {[
              { id: 'generator', label: 'مولد التقارير', icon: '' },
              { id: 'history', label: 'سجل التقارير الأخرى (غير المرتبطة بالاستوديو)', icon: '' }
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`reports-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Report Generator Tab */}
          {activeTab === 'generator' && (
            <div className="space-y-6">
              {/* Report Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">نوع التقرير</label>
                  <select
                    data-testid="report-type-select"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="overview">إحصائيات عامة</option>
                    <option value="comprehensive_detailed">تقرير شامل مفصل</option>
                    <option value="detailed">تقرير مفصل</option>
                    <option value="executive_brief">تقرير تنفيذي مختصر</option>
                    <option value="technical_detailed">تقرير فني مفصل</option>
                    <option value="performance_achievements">تقرير الأداء والإنجازات</option>
                    <option value="time_comparisons">تقرير مقارنات زمنية</option>
                    <option value="players">تقرير اللاعبين</option>
                    <option value="home_matches">تقرير المباريات داخل الأرض</option>
                    <option value="away_matches">تقرير المباريات خارج الأرض</option>
                    <option value="matches">تقرير المباريات</option>
                    <option value="match_results">تقرير نتائج المباريات</option>
                    <option value="match_schedule">تقرير جدول المباريات</option>
                    <option value="scorers">تقرير الهدافين</option>
                    <option value="comprehensive">تقرير شامل</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">النطاق الزمني</label>
                  <select
                    data-testid="report-date-range-select"
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">جميع الفترات</option>
                    <option value="last30">آخر 30 يوم</option>
                    <option value="last90">آخر 90 يوم</option>
                    <option value="custom">فترة مخصصة</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الموسم</label>
                  <select
                    data-testid="report-season-select"
                    value={seasonId || 'all'}
                    onChange={(e) => onSeasonChange && onSeasonChange(e.target.value)}
                    disabled={!onSeasonChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="all">كل المواسم (الأرشيف الكامل)</option>
                    <option value="unlinked">غير مرتبطة بموسم</option>
                    {seasons.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}{s.status === 'active' ? ' (نشط)' : ''}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">الفئة</label>
                  <select
                    data-testid="report-category-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">جميع الفئات</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {dateRange === 'custom' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">من تاريخ</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">إلى تاريخ</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleGenerateReport}
                  data-testid="generate-report-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center"
                >
                  <span className="mr-2"></span>
                  إنشاء التقرير
                </button>

                {reportData && (
                  <>
                    <button
                      onClick={reportType === 'players' ? exportPlayersReportPdf : exportToPDF}
                      disabled={loading}
                      data-testid="export-pdf-button"
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-60"
                    >
                      {loading ? <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> جاري التصدير...</> : <>تصدير PDF</>}
                    </button>

                    <button
                      onClick={reportType === 'players' ? exportPlayersReportExcel : exportToExcel}
                      data-testid="export-excel-button"
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center"
                    >
                      <span className="mr-2"></span>
                      تصدير Excel
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* استوديو التقارير انتقل إلى تبويب مستقل في مركز التقارير (بعد الحوكمة والإدارة) */}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              {exportLog.length === 0 ? (
                <div className="text-center py-8" data-testid="export-log-empty">
                  <div className="text-gray-400 text-6xl mb-4">📋</div>
                  <h3 className="text-lg font-medium text-gray-900">سجل التقارير</h3>
                  <p className="text-gray-500">لا يوجد أي تصدير بعد — سيظهر هنا كل ملف PDF أو Excel تصدّره</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">سجل التصدير ({exportLog.length})</h3>
                    <button
                      onClick={clearExportLog}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                      data-testid="export-log-clear-btn"
                    >
                      مسح السجل
                    </button>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full text-sm" data-testid="export-log-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-right font-semibold text-gray-700">التاريخ والوقت</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-700">نوع التقرير</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-700">الصيغة</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-700">اسم الملف</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {exportLog.map((entry) => (
                          <tr key={entry.id} data-testid="export-log-row">
                            <td className="px-4 py-2 text-gray-600">{new Date(entry.date).toLocaleString('ar-SA')}</td>
                            <td className="px-4 py-2 text-gray-800">{entry.reportTypeLabel}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${entry.format === 'PDF' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                {entry.format}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-500 truncate max-w-xs" title={entry.fileName}>{entry.fileName}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Report Content */}
      {reportData && (
        <div ref={reportContentRef} className="bg-white rounded-lg shadow p-8" id="reportContent">
          {reportType === 'players' && playersStudioSettings ? (
            <ReportPrintFrame settings={playersStudioSettings} identity={clubInfo} reportTitle="تقرير اللاعبين">
              {renderReportContent()}
            </ReportPrintFrame>
          ) : generalStudioSettings ? (
            <ReportPrintFrame settings={generalStudioSettings} identity={clubInfo} reportTitle={getReportTypeArabic(reportType)}>
              {renderReportContent()}
            </ReportPrintFrame>
          ) : (
            renderReportContent()
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedAdvancedReportsSection;